import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'ja' | 'en';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
}

const translations = {
    ja: {
        // ゲーム全般
        'game.title': 'オンラインリバーシ',
        'game.gameId': 'ゲームID',
        'game.yourColor': 'あなたの色',
        'game.black': '黒',
        'game.white': '白',
        'game.spectator': '観戦者',
        'game.loading': 'ゲームを読み込み中...',
        'game.noGameData': 'ゲームデータがありません。',
        'game.backToLobby': 'ロビーに戻る',
        'game.leaveGame': 'ゲームを離れる',
        'game.startGame': 'ゲームを始める',
        'game.enterPlayerName': 'プレイヤー名を入力してください',
        'game.yourNamePlaceholder': 'あなたの名前',

        // プレイヤー情報
        'player.blackPlayer': '黒プレイヤー',
        'player.whitePlayer': '白プレイヤー',
        'player.score': 'スコア',
        'player.player1': 'プレイヤー1',
        'player.player2': 'プレイヤー2',
        'player.notJoined': '未参加',

        // ゲーム状態
        'status.gameState': 'ゲームの状態',
        'status.waitingForPlayer': '相手プレイヤーを待っています...',
        'status.yourTurn': 'あなたのターンです！',
        'status.blackTurn': '黒のターンです。',
        'status.whiteTurn': '白のターンです。',
        'status.gameFinished': 'ゲーム終了！',
        'status.draw': '引き分け',
        'status.blackWins': '黒の勝利！',
        'status.whiteWins': '白の勝利！',
        'status.congratulations': 'おめでとうございます！',
        'status.betterLuckNextTime': '残念でした...',

        // エラーメッセージ
        'error.notYourTurn': '今はあなたのターンではありません。',
        'error.invalidMove': 'そこには置けません。有効なマスを選んでください。',
        'error.moveFailed': '手を打つのに失敗しました。元の状態に戻します。',
        'error.leaveFailed': 'ゲームを離れる際にエラーが発生しました。',
        'error.fetchFailed': 'ゲームデータの取得に失敗しました。',
        'error.enterPlayerName': 'プレイヤー名を入力してください。',
        'error.enterGameId': 'ゲームIDを入力してください。',

        // ロビー
        'lobby.title': 'ゲームロビー',
        'lobby.playerName': 'あなたのプレイヤー名',
        'lobby.createGame': '新しいゲームを作成',
        'lobby.creating': '作成中...',
        'lobby.joinGame': 'ゲーム参加',
        'lobby.gameList': '利用可能なゲーム',
        'lobby.sessionId': 'セッションID',
        'lobby.refresh': '更新',
        'lobby.joinByGameId': 'ゲームIDで参加',
        'lobby.gameIdPlaceholder': 'ゲームIDを入力...',
        'lobby.join': '参加',
        'lobby.delete': '削除',
        'lobby.creator': '作成者',
        'lobby.playerCount': 'プレイヤー数',
        'lobby.status': '状態',
        'lobby.statusWaiting': '待機中',
        'lobby.statusPlaying': 'プレイ中',
        'lobby.statusFinished': '終了',
        'lobby.noGamesAvailable': '現在、利用可能なゲームはありません。',

        // その他
        'common.language': '言語',
        'common.loading': '読み込み中...',
        'common.copy': 'コピー',
        'common.copied': 'コピーしました！',
        'common.clickToCopy': 'クリックでコピー',
        'common.initializing': '初期化中...'
    } as { [key: string]: string },
    en: {
        // Game general
        'game.title': 'Online Reversi',
        'game.gameId': 'Game ID',
        'game.yourColor': 'Your Color',
        'game.black': 'Black',
        'game.white': 'White',
        'game.spectator': 'Spectator',
        'game.loading': 'Loading game...',
        'game.noGameData': 'No game data available.',
        'game.backToLobby': 'Back to Lobby',
        'game.leaveGame': 'Leave Game',
        'game.startGame': 'Start Game',
        'game.enterPlayerName': 'Please enter your player name',
        'game.yourNamePlaceholder': 'Your name',

        // Player info
        'player.blackPlayer': 'Black Player',
        'player.whitePlayer': 'White Player',
        'player.score': 'Score',
        'player.player1': 'Player 1',
        'player.player2': 'Player 2',
        'player.notJoined': 'Not Joined',

        // Game status
        'status.gameState': 'Game State',
        'status.waitingForPlayer': 'Waiting for opponent...',
        'status.yourTurn': 'Your turn!',
        'status.blackTurn': "Black's turn.",
        'status.whiteTurn': "White's turn.",
        'status.gameFinished': 'Game Finished!',
        'status.draw': 'Draw',
        'status.blackWins': 'Black Wins!',
        'status.whiteWins': 'White Wins!',
        'status.congratulations': 'Congratulations!',
        'status.betterLuckNextTime': 'Better luck next time...',

        // Error messages
        'error.notYourTurn': "It's not your turn.",
        'error.invalidMove': 'Invalid move. Please select a valid position.',
        'error.moveFailed': 'Failed to make move. Reverting to original state.',
        'error.leaveFailed': 'Error occurred while leaving the game.',
        'error.fetchFailed': 'Failed to fetch game data.',
        'error.enterPlayerName': 'Please enter a player name.',
        'error.enterGameId': 'Please enter a Game ID.',

        // Lobby
        'lobby.title': 'Game Lobby',
        'lobby.playerName': 'Your Player Name',
        'lobby.createGame': 'Create New Game',
        'lobby.creating': 'Creating...',
        'lobby.joinGame': 'Join Game',
        'lobby.gameList': 'Available Games',
        'lobby.sessionId': 'Session ID',
        'lobby.refresh': 'Refresh',
        'lobby.joinByGameId': 'Join by Game ID',
        'lobby.gameIdPlaceholder': 'Enter Game ID...',
        'lobby.join': 'Join',
        'lobby.delete': 'Delete',
        'lobby.creator': 'Creator',
        'lobby.playerCount': 'Players',
        'lobby.status': 'Status',
        'lobby.statusWaiting': 'Waiting',
        'lobby.statusPlaying': 'Playing',
        'lobby.statusFinished': 'Finished',
        'lobby.noGamesAvailable': 'No games are currently available.',

        // Others
        'common.language': 'Language',
        'common.loading': 'Loading...',
        'common.copy': 'Copy',
        'common.copied': 'Copied!',
        'common.clickToCopy': 'Click to copy',
        'common.initializing': 'Initializing...'
    } as { [key: string]: string }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>('ja');

    useEffect(() => {
        const savedLanguage = localStorage.getItem('reversi-language') as Language;
        if (savedLanguage && (savedLanguage === 'ja' || savedLanguage === 'en')) {
            setLanguageState(savedLanguage);
        }
    }, []);

    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem('reversi-language', lang);
    };

    const t = (key: string): string => {
        return translations[language][key] || key;
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
}; 